import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { threadKey, bumpUnread, clearUnread, sortUnreadFirst, type ChatMessageFacts } from "@/lib/chat/unread";
import { needsDivider, dayDividerLabel } from "@/lib/chat/day-divider";
import { ChatAttachment, MAX_ATTACH_BYTES, formatBytes, pastedImage } from "@/components/ChatAttachment";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import type { ChatChannel, ChatMessage, Employee } from "@/types/db";
import { fmtTime, initials } from "@/lib/utils";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Video, Copy as CopyIcon, Menu, X, Paperclip, FileText, Download, Users, Reply, Smile } from "lucide-react";
import { CloseButton } from "@/components/CloseButton";
import { fetchDmRecents, sortByRecent, bumpRecent } from "@/lib/chat/recents";

interface Conversation {
  kind: "channel" | "dm";
  id: string;
  name: string;
}

const MENTION_RE = /@([a-zA-Z0-9_.-]+)/g;

// Roles that can edit any group chat's roster/name, mirroring hq_chat_group_lead()
// in sql/2026-09-09_chat_group_manage.sql. Deleting a group stays with its owner
// (plus super_admin); both rules are re-checked server-side by the RPCs.
const GROUP_LEAD_ROLES = ["admin", "super_admin", "manager", "c_suite"];

// Curated, fixed set — this is a quick way to drop a common emoji into a
// message, not a searchable emoji database. If someone wants an emoji not on
// this list, their OS picker still works; the composer already sends and
// renders whatever character lands in the textarea.
const EMOJI_LIST = [
  "\ud83d\ude00", "\ud83d\ude02", "\ud83d\ude05", "\ud83d\ude0a", "\ud83d\ude0d", "\ud83d\ude0e", "\ud83e\udd14", "\ud83d\ude22",
  "\ud83d\ude21", "\ud83d\udc4d", "\ud83d\udc4e", "\ud83d\udc4f", "\ud83d\ude4c", "\ud83d\ude4f", "\ud83d\udcaa", "\ud83d\udd25",
  "\ud83c\udf89", "\u2728", "\ud83d\udcaf", "\u2764\ufe0f", "\ud83d\udc94", "\ud83d\ude34", "\ud83e\udd1d", "\ud83d\udc40",
  "\ud83d\ude80", "\u2705", "\u274c", "\u26a0\ufe0f", "\ud83d\udccc", "\ud83d\udcce", "\ud83c\udfaf", "\ud83d\udca1",
  "\u23f0", "\ud83d\udcc5", "\ud83c\udf55", "\u2615", "\ud83c\udf82", "\ud83e\udd73", "\ud83d\ude0c", "\ud83d\ude4b",
];

/** Roughly seven lines. Past this the composer scrolls instead of growing. */
const COMPOSER_MAX_PX = 168;

export function Chat() {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [people, setPeople] = useState<Employee[]>([]);
  const [active, setActive] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [mentionState, setMentionState] = useState<{ open: boolean; query: string; start: number }>({ open: false, query: "", start: 0 });
  const [mentionIdx, setMentionIdx] = useState(0);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [huddleInfo, setHuddleInfo] = useState<{ url: string } | null>(null);
  const [huddlePicker, setHuddlePicker] = useState(false);
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(!isMobile);
  const [groupModal, setGroupModal] = useState(false);
  // thread key -> unread count. Server-computed on load (so it survives a
  // refresh), folded forward by the realtime stream between loads.
  const [unread, setUnread] = useState<Record<string, number>>({});
  // thread key -> when it last spoke (ms). Only consulted for ordering the
  // unread ones; read threads keep the list's own alphabetical order.
  const [latest, setLatest] = useState<Record<string, number>>({});
  // When I last talked to each person: the direct messages list reads newest conversation first.
  const [recents, setRecents] = useState<Record<string, number>>({});
  const [manageGroup, setManageGroup] = useState<ChatChannel | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Sync sidebar open state when viewport class flips
  useEffect(() => { setSidebarOpen(!isMobile); }, [isMobile]);

  // When picking a conversation on mobile, auto-close the sidebar overlay
  const activeKey = active ? threadKey(active.kind, active.id) : null;

  async function refreshUnread() {
    const { data, error } = await supabase.rpc("hq_chat_unread_threads");
    // The function arrives with 2026-09-08_chat_unread.sql; until it is applied
    // the sidebar simply shows no indicators rather than an error.
    if (error) return;
    const rows = (data as { thread_key: string; unread: number; latest: string | null }[]) ?? [];
    setUnread(Object.fromEntries(rows.map(r => [r.thread_key, Number(r.unread)])));
    setLatest(Object.fromEntries(rows.map(r => [r.thread_key, Date.parse(r.latest ?? "") || 0])));
  }

  // Opening a conversation IS reading it. The mark is written server-side so
  // it survives refreshes and other devices; the local count clears at once.
  function markRead(key: string) {
    setUnread(prev => clearUnread(prev, key));
    if (!employee) return;
    void supabase.from("hq_chat_reads").upsert(
      { employee_id: employee.id, thread_key: key, last_read_at: new Date().toISOString() },
      { onConflict: "employee_id,thread_key" });
    // The header bubble counts unread chat NOTIFICATIONS — a separate ledger
    // from the read marks above, and reading a conversation here must settle
    // both, or the badge advertises a message the sidebar rightly shows as
    // read. Same link-shape the dock uses when it does this.
    const [kind, id] = key.split(":");
    const link = kind === "dm" ? `/chat?dm=${id}` : `/chat?channel=${id}`;
    void supabase.from("hq_notifications").update({ read_at: new Date().toISOString() })
      .eq("recipient_id", employee.id).is("read_at", null).eq("link", link);
  }

  function pickConversation(c: Conversation) {
    setActive(c);
    markRead(threadKey(c.kind, c.id));
    if (isMobile) setSidebarOpen(false);
  }

  useEffect(() => { void loadSidebar(); void refreshUnread(); }, [employee?.id]);
  useEffect(() => { if (active) void loadMessages(); }, [active?.id, active?.kind]);

  useEffect(() => {
    if (!active || !employee) return;
    const channel = supabase
      .channel(`hq-chat-${active.kind}-${active.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "hq_chat_messages" },
        (payload) => {
          const m = payload.new as ChatMessage;
          if (m.is_direct_message && (m.sender_id === employee.id || m.recipient_id === employee.id)) {
            const other = m.sender_id === employee.id ? m.recipient_id : m.sender_id;
            if (other) setRecents(prev => bumpRecent(prev, other));
          }
          if (active.kind === "channel" && m.channel_id === active.id) {
            setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
            markRead(threadKey("channel", active.id));
          } else if (active.kind === "dm") {
            const pair = (m.sender_id === employee.id && m.recipient_id === active.id) ||
                         (m.sender_id === active.id && m.recipient_id === employee.id);
            if (pair) {
              setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
              markRead(threadKey("dm", active.id));
              return;
            }
            setUnread(prev => bumpUnread(prev, m as ChatMessageFacts, employee.id, threadKey("dm", active.id)));
            setLatest(prev => ({ ...prev, [`dm:${m.sender_id}`]: Date.now() }));
          } else {
            setUnread(prev => bumpUnread(prev, m as ChatMessageFacts, employee.id, activeKey));
            if (m.channel_id) setLatest(prev => ({ ...prev, [`channel:${m.channel_id}`]: Date.now() }));
          }
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [active?.id, active?.kind, employee?.id]);

  // Jump to the bottom when the NEWEST message changes — not on any length
  // change. Prepending a page of older history also changes the length, and
  // scrolling to the bottom then would throw the reader out of the history they
  // just asked for (HQ-CHAT-002).
  const newestId = messages.length ? messages[messages.length - 1].id : null;
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [newestId]);

  async function loadSidebar() {
    const [{ data: chs }, { data: ppl }] = await Promise.all([
      supabase.from("hq_chat_channels").select("*").order("name"),
      supabase.from("hq_directory").select("*").order("first_name"),
    ]);
    const channelList = (chs as ChatChannel[]) ?? [];
    const peopleList = ((ppl as Employee[]) ?? []).filter(p => p.id !== employee?.id);
    setChannels(channelList);
    setPeople(peopleList);
    if (employee) setRecents(await fetchDmRecents(employee.id));
    if (active) return;

    // Honor URL params (?dm=<employee_id> or ?channel=<channel_id>) from notification links
    const dmId = searchParams.get("dm");
    const chId = searchParams.get("channel");
    if (dmId) {
      const peer = peopleList.find(p => p.id === dmId);
      if (peer) {
        setActive({ kind: "dm", id: peer.id, name: `${peer.first_name ?? peer.email} ${peer.last_name ?? ""}`.trim() });
        setSearchParams({}, { replace: true });
        return;
      }
    }
    if (chId) {
      const ch = channelList.find(c => c.id === chId);
      if (ch) {
        setActive({ kind: "channel", id: ch.id, name: "#" + ch.name });
        setSearchParams({}, { replace: true });
        return;
      }
    }
    // Default: first channel (general before announcements where possible)
    const general = channelList.find(c => c.name === "general") ?? channelList[0];
    if (general) setActive({ kind: "channel", id: general.id, name: "#" + general.name });
  }

  /** One page of history. 500 was the whole conversation; now it is a window. */
  const PAGE = 500;

  /** Scopes a query to the open conversation — channel or DM pair. */
  function scopeToConversation<T>(q: T): T {
    const b = q as unknown as {
      eq: (c: string, v: unknown) => unknown;
      or: (f: string) => unknown;
    };
    if (!active || !employee) return q;
    if (active.kind === "channel") {
      return (b.eq("channel_id", active.id) as typeof b).eq("is_direct_message", false) as T;
    }
    return (b.eq("is_direct_message", true) as typeof b).or(
      `and(sender_id.eq.${employee.id},recipient_id.eq.${active.id}),and(sender_id.eq.${active.id},recipient_id.eq.${employee.id})`,
    ) as T;
  }

  // HQ-CHAT-002. This asked for the OLDEST 500 — ascending with a limit — so
  // once a conversation passed 500 messages the page deterministically showed
  // the beginning of it and everything recent was invisible, including messages
  // sent seconds earlier. Opening a conversation now asks for the NEWEST page,
  // descending, and reverses it for display; older messages load on demand.
  async function loadMessages() {
    if (!active || !employee) return;
    const q = scopeToConversation(
      supabase.from("hq_chat_messages").select("*").is("deleted_at", null)
        .order("created_at", { ascending: false }).limit(PAGE));
    const { data } = await q;
    const page = ((data as ChatMessage[]) ?? []).slice().reverse();
    setMessages(page);
    setHasOlder(page.length === PAGE);
  }

  /** Prepend the page before the oldest message on screen. */
  async function loadOlder() {
    if (!active || !employee || loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const oldest = messages[0];
    const q = scopeToConversation(
      supabase.from("hq_chat_messages").select("*").is("deleted_at", null)
        .lt("created_at", oldest.created_at)
        .order("created_at", { ascending: false }).limit(PAGE));
    const { data } = await q;
    const older = ((data as ChatMessage[]) ?? []).slice().reverse();
    // Keep the reader where they are: measure the scroll height before the
    // prepend and restore the offset after it, or the view jumps to the top.
    const el = scrollRef.current;
    const before = el ? el.scrollHeight - el.scrollTop : 0;
    setMessages(prev => {
      const seen = new Set(prev.map(m => m.id));
      return [...older.filter(m => !seen.has(m.id)), ...prev];
    });
    setHasOlder(older.length === PAGE);
    setLoadingOlder(false);
    requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - before; });
  }

  // Extract mentions from a message and return matched employee IDs
  function extractMentions(text: string): { ids: string[] } {
    const matches = [...text.matchAll(MENTION_RE)];
    const ids: string[] = [];
    for (const m of matches) {
      const tag = m[1].toLowerCase();
      const emp = people.find(p =>
        p.username?.toLowerCase() === tag ||
        (p.first_name && p.first_name.toLowerCase() === tag) ||
        ((p.first_name && p.last_name) && `${p.first_name.toLowerCase()}${p.last_name.toLowerCase()}` === tag)
      );
      if (emp) ids.push(emp.id);
    }
    return { ids: [...new Set(ids)] };
  }

  function pickFile(f: File | null) {
    if (!f) { setPendingFile(null); return; }
    if (f.size > MAX_ATTACH_BYTES) {
      setSendError(`"${f.name}" is too large (max 25 MB).`);
      return;
    }
    setSendError(null);
    setPendingFile(f);
  }

  async function send() {
    if ((!draft.trim() && !pendingFile) || !active || !employee) return;
    const content = draft.trim();
    setSending(true);
    setSendError(null);

    // Upload the attachment first (if any) so the message row carries its path.
    let attach: Record<string, unknown> = {};
    if (pendingFile) {
      const safeName = pendingFile.name.replace(/[^\w.\-]+/g, "_");
      const path = `${employee.id}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from("chat-attachments")
        .upload(path, pendingFile, { contentType: pendingFile.type || undefined, upsert: false });
      if (upErr) {
        setSending(false);
        setSendError("Upload failed: " + upErr.message);
        return;
      }
      attach = {
        attachment_path: path,
        attachment_name: pendingFile.name,
        attachment_type: pendingFile.type || null,
        attachment_size: pendingFile.size,
      };
    }

    const row: Record<string, unknown> = {
      sender_id: employee.id,
      content,
      is_direct_message: active.kind === "dm",
      reply_to_id: replyingTo?.id ?? null,
      ...attach,
    };
    if (active.kind === "channel") row.channel_id = active.id;
    else row.recipient_id = active.id;
    let { data: inserted, error } = await supabase.from("hq_chat_messages").insert(row).select("id").single();

    // ── A REFUSED GROUP POST IS A MEMBERSHIP PROBLEM, NOT A SQL LESSON ────
    // Group posting is membership-only by design (HQ-CHAT-003), and leadership
    // reads channels it never joined — so the composer can hit row-level
    // security. Whoever may manage the group's roster may join it
    // (hq_join_group_chat), so try exactly that once and retry; everyone else
    // gets the refusal in words a person can act on, not the policy's name.
    if (error && active.kind === "channel" && /row-level security/i.test(error.message ?? "")) {
      const { data: joined } = await supabase.rpc("hq_join_group_chat", { p_channel: active.id });
      if (joined === true) {
        ({ data: inserted, error } = await supabase.from("hq_chat_messages").insert(row).select("id").single());
      } else {
        setSending(false);
        setSendError("You're not a member of this group, so it won't accept your message. Ask a group lead to add you.");
        return;
      }
    }
    setSending(false);
    if (error) {
      console.error("[chat] send failed:", error);
      setSendError(/row-level security/i.test(error.message ?? "")
        ? "This conversation won't accept the message — you may not be a member. Ask a group lead to add you."
        : (error.message || "Could not send. Check your connection."));
      return;
    }
    if (!inserted) {
      setSendError("Message sent but not returned. Try again.");
      return;
    }
    const messageId = (inserted as { id: string }).id;

    // Insert mentions (fires DB trigger → notification)
    const { ids } = extractMentions(draft);
    if (ids.length > 0) {
      await supabase.from("hq_chat_mentions").insert(
        ids.map(id => ({ message_id: messageId, mentioned_employee_id: id, mentioner_id: employee.id }))
      );
    }

    // Team-channel messages notify the channel's other participants (like a
    // group thread). DMs are handled by a DB trigger; @mentioned people already
    // get a mention notification above, so exclude them to avoid a double ping.
    if (active.kind === "channel") {
      const preview = content || (pendingFile ? `📎 ${pendingFile.name}` : "");
      await notifyChannel(active.id, active.name, preview, messageId, new Set(ids));
    }

    setDraft("");
    setPendingFile(null);
    setReplyingTo(null);
    if (fileRef.current) fileRef.current.value = "";
    setSendError(null);
    setMentionState({ open: false, query: "", start: 0 });
  }

  // Notify everyone who has participated in this channel (excluding the sender
  // and anyone just @mentioned). Participation = has posted in the channel.
  async function notifyChannel(channelId: string, channelName: string, content: string, messageId: string, mentioned: Set<string>) {
    if (!employee) return;
    const { data: parts } = await supabase
      .from("hq_chat_messages")
      .select("sender_id")
      .eq("channel_id", channelId)
      .eq("is_direct_message", false);
    const recipients = [...new Set((parts ?? []).map(p => (p as { sender_id: string }).sender_id))]
      .filter(rid => rid && rid !== employee.id && !mentioned.has(rid));
    if (recipients.length === 0) return;
    await supabase.from("hq_notifications").insert(recipients.map(rid => ({
      recipient_id: rid,
      title: channelName,
      preview: `${employee.first_name ?? "Someone"}: ${content.slice(0, 120)}`,
      category: "chat_channel",
      link: `/chat?channel=${channelId}`,
      source_id: messageId,
    })));
  }

  async function deleteMessage(id: string) {
    if (!confirm("Delete this message?")) return;
    await supabase.from("hq_chat_messages").update({ deleted_at: new Date().toISOString() }).eq("id", id);
    setMessages(prev => prev.filter(m => m.id !== id));
  }

  function startHuddle() {
    if (!active || !employee) return;
    setHuddlePicker(true);
  }

  async function launchHuddle(invitedIds: string[]) {
    if (!active || !employee) return;
    // Generate a unique Jitsi room URL. Same URL for host and every invitee
    // means everyone lands in the SAME room. No paste-back, no auth dance.
    const roomId = `scenta-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const meetUrl = `https://meet.jit.si/${roomId}`;
    window.open(meetUrl, "_blank", "noopener");
    setHuddleInfo({ url: meetUrl });

    // Post a chat message announcing the huddle
    const content = `🎥 Huddle started — join here: ${meetUrl}`;
    const row: Record<string, unknown> = {
      sender_id: employee.id,
      content,
      is_direct_message: active.kind === "dm",
    };
    if (active.kind === "channel") row.channel_id = active.id;
    else row.recipient_id = active.id;
    await supabase.from("hq_chat_messages").insert(row);

    // Notify each invited person with the REAL meet URL
    if (invitedIds.length > 0) {
      await supabase.from("hq_notifications").insert(
        invitedIds.filter(id => id !== employee.id).map(id => ({
          recipient_id: id,
          preview: `🎥 ${employee.first_name ?? "Someone"} started a huddle — tap to join`,
          category: "huddle_invite",
          link: meetUrl,
          source_id: null,
        }))
      );
    }
    setHuddlePicker(false);
  }

  function onDraftChange(value: string, cursor: number) {
    setDraft(value);
    // Check if cursor is in an @mention context
    const upToCursor = value.slice(0, cursor);
    const match = upToCursor.match(/@([a-zA-Z0-9_.-]*)$/);
    if (match) {
      setMentionState({ open: true, query: match[1], start: cursor - match[0].length });
      setMentionIdx(0);
    } else {
      setMentionState({ open: false, query: "", start: 0 });
    }
  }

  // ── WHY THE COMPOSER IS A TEXTAREA THAT RESIZES ITSELF ──────────────────
  // It was an `<input>`, which is one line and stays one line however much you
  // type: past the width of the box the start of your own message scrolls out
  // of sight, so you cannot read back what you are about to send. Nothing was
  // broken in a way a test would catch — the value was always correct — and
  // that is why it survived. You could only see it by typing.
  //
  // A textarea also makes the Enter handling below honest. It already treated
  // Shift+Enter as "not a submit", which in an `<input>` meant Shift+Enter did
  // nothing at all. Now it inserts the newline the code always implied.
  //
  // The height is measured, not guessed: reset to `auto` first so scrollHeight
  // reports what the text needs rather than the height we last set, then grow
  // to it up to COMPOSER_MAX_PX and scroll beyond that — so a pasted essay
  // cannot push the send button off a phone screen.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const fit = () => {
      // Read before the reflow below destroys both.
      const atEnd = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
      const prevTop = el.scrollTop;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_PX)}px`;
      const over = el.scrollHeight > COMPOSER_MAX_PX;
      el.style.overflowY = over ? "auto" : "hidden";
      // ── PAST THE CAP, KEEP THE CARET IN VIEW ──────────────────────────
      // Setting the height to `auto` is what makes the measurement honest, and
      // it also throws away the scroll position the browser maintains to follow
      // the caret. So beyond COMPOSER_MAX_PX the line being typed sat about
      // thirteen pixels below the fold: the original complaint — you cannot see
      // what you are writing — moved from line one to line eight.
      //
      // Measured in the live composer at 706 characters: scrollHeight 188,
      // clientHeight 168, scrollTop 6.5, caret at the end.
      //
      // Pinned to the bottom only when the caret is at the end, which is what
      // typing means. If it is anywhere else the reader has scrolled up on
      // purpose, and the previous position is restored instead of yanked.
      if (over) el.scrollTop = atEnd ? el.scrollHeight : prevTop;
    };
    fit();
    // A width change rewraps the text, so the height it needs changes with no
    // keystroke to trigger it. Rotating a phone is the common case.
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [draft, active?.id]);

  const mentionMatches = useMemo(() => {
    if (!mentionState.open) return [];
    const q = mentionState.query.toLowerCase();
    return people
      .filter(p => {
        const candidates = [p.username, p.first_name, p.last_name, p.email].filter(Boolean).map(s => s!.toLowerCase());
        return candidates.some(c => c.includes(q));
      })
      .slice(0, 6);
  }, [mentionState.open, mentionState.query, people]);

  function pickMention(emp: Employee) {
    const tag = emp.username || emp.first_name || emp.email.split("@")[0];
    const before = draft.slice(0, mentionState.start);
    const after = draft.slice(mentionState.start + mentionState.query.length + 1);
    const next = `${before}@${tag} ${after}`;
    setDraft(next);
    setMentionState({ open: false, query: "", start: 0 });
    setTimeout(() => { inputRef.current?.focus(); }, 0);
  }

  // Inserts an emoji at the composer's current cursor position, same shape as
  // pickMention above: splice the draft, then restore focus. Unlike a mention
  // (which always lands at a known offset because it replaces a typed "@tag"),
  // the emoji button lives outside the textarea, so the cursor is read off the
  // DOM element directly — it survives the button's mousedown because that
  // handler calls preventDefault, which stops the browser from moving focus
  // (and therefore the selection) away from the textarea in the first place.
  function insertEmoji(emoji: string) {
    const el = inputRef.current;
    const cursor = el?.selectionStart ?? draft.length;
    const next = draft.slice(0, cursor) + emoji + draft.slice(cursor);
    setDraft(next);
    setEmojiPickerOpen(false);
    const caret = cursor + emoji.length;
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(caret, caret);
    }, 0);
  }

  function renderMessageContent(text: string): React.ReactNode {
    const parts: React.ReactNode[] = [];
    let lastIdx = 0;
    const linkRe = /(https?:\/\/[^\s]+)/g;
    const tokens: Array<{ kind: "text" | "mention" | "link"; value: string; start: number; end: number; }> = [];
    for (const m of text.matchAll(MENTION_RE)) {
      tokens.push({ kind: "mention", value: m[1], start: m.index!, end: m.index! + m[0].length });
    }
    for (const m of text.matchAll(linkRe)) {
      tokens.push({ kind: "link", value: m[1], start: m.index!, end: m.index! + m[0].length });
    }
    tokens.sort((a, b) => a.start - b.start);
    for (const t of tokens) {
      if (t.start < lastIdx) continue;
      if (t.start > lastIdx) parts.push(text.slice(lastIdx, t.start));
      if (t.kind === "mention") {
        const target = t.value.toLowerCase();
        const emp = people.find(p =>
          p.username?.toLowerCase() === target ||
          (p.first_name && p.first_name.toLowerCase() === target)
        );
        if (emp) {
          parts.push(<span key={`m-${t.start}`} className="rounded bg-teal-mint/60 px-1 font-semibold text-teal-dark">@{t.value}</span>);
        } else {
          parts.push(`@${t.value}`);
        }
      } else {
        parts.push(<a key={`l-${t.start}`} href={t.value} target="_blank" rel="noreferrer" className="underline">{t.value}</a>);
      }
      lastIdx = t.end;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    return <>{parts}</>;
  }

  const peopleMap = useMemo(() => Object.fromEntries(people.map(p => [p.id, p])), [people]);
  // Channels: whoever is waiting on you comes to you — unread float to the top, freshest
  // first; everything read keeps its alphabetical place.
  // Direct messages read like a messenger inbox: the person you talked to most recently
  // is first, then everyone you have not messaged, alphabetically.
  const sortedChannels = useMemo(
    () => sortUnreadFirst(channels, ch => threadKey("channel", ch.id), unread, latest),
    [channels, unread, latest]);
  const sortedPeople = useMemo(
    () => sortByRecent(people, p => p.id, recents),
    [people, recents]);
  const messagesById = useMemo(() => new Map(messages.map(m => [m.id, m])), [messages]);

  // First name of a message's sender (falls back to email/"Someone").
  function senderName(m: ChatMessage): string {
    const s = m.sender_id === employee?.id ? employee : peopleMap[m.sender_id];
    return s?.first_name || s?.email || "Someone";
  }

  // Short one-line snippet of a message for reply previews.
  function snippet(m: ChatMessage): string {
    const base = m.content?.trim() || (m.attachment_name ? `📎 ${m.attachment_name}` : "");
    return base.length > 80 ? base.slice(0, 80) + "…" : base;
  }

  // The one line of text identifying this control, used both as the visible
  // placeholder and — because a placeholder disappears on focus/typing and
  // several screen readers never announce it at all — as its accessible name.
  const composerLabel = active ? t("chat.messagePlaceholder", { name: active.name }) : t("chat.selectConversation");

  // The open conversation as a channel row (groups carry created_by = owner).
  const activeChannel = active?.kind === "channel" ? channels.find(c => c.id === active.id) ?? null : null;
  const activeGroup = activeChannel?.is_group ? activeChannel : null;
  // Roster edits: the owner, or any admin/manager. Deleting: the owner only
  // (super_admin can too — the RPC enforces both rules server-side).
  const canManageGroup = !!activeGroup && (
    activeGroup.created_by === employee?.id || GROUP_LEAD_ROLES.includes(employee?.role ?? "")
  );

  return (
    <div className="mx-auto -m-5 flex h-[calc(100vh-4rem)] overflow-hidden md:-m-8 md:h-[calc(100vh-4rem)]">
      {/* Mobile overlay backdrop when sidebar is open */}
      {isMobile && sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-ink/60" onClick={() => setSidebarOpen(false)} />
      )}
      <aside
        className={`overflow-y-auto border-r border-line bg-white p-3 transition-transform md:relative md:z-auto md:w-64 md:shrink-0 md:translate-x-0 ${
          isMobile
            ? `fixed inset-y-0 left-0 z-40 w-64 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`
            : ""
        }`}
      >
        <div className="mb-2 flex items-center justify-between px-2 md:hidden">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray2">Chat</span>
          <button onClick={() => setSidebarOpen(false)} className="text-gray2 hover:text-charcoal"><X size={18} /></button>
        </div>
        <div className="mb-2 flex items-center justify-between px-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray2">{t("chat.channels")}</span>
          <button onClick={() => setGroupModal(true)} title="New group chat" className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-teal-dark hover:bg-offwhite"><Users size={13} /> New group</button>
        </div>
        {sortedChannels.map(ch => {
          const isGroup = (ch as { is_group?: boolean }).is_group;
          const hasUnread = (unread[threadKey("channel", ch.id)] ?? 0) > 0;
          return (
            <button
              key={ch.id}
              onClick={() => pickConversation({ kind: "channel", id: ch.id, name: (isGroup ? "" : "#") + (ch.name || "Group chat") })}
              className={`flex w-full items-center gap-1.5 rounded-lg px-3 py-1.5 text-left text-sm transition ${
                active?.kind === "channel" && active.id === ch.id
                  ? "bg-teal-mint text-teal-dark font-semibold"
                  : `hover:bg-offwhite ${hasUnread ? "font-semibold text-charcoal" : "text-charcoal"}`
              }`}
            >
              {isGroup ? <Users size={14} className="shrink-0 text-gray2" /> : <span className="text-gray2">#</span>}
              <span className="truncate">{ch.name || "Group chat"}</span>
              {hasUnread && <span aria-label="unread" className="ml-auto h-2 w-2 shrink-0 rounded-full bg-teal-dark" />}
            </button>
          );
        })}
        <div className="mb-2 mt-4 px-2 text-xs font-semibold uppercase tracking-wider text-gray2">{t("chat.directMessages")}</div>
        {people.length === 0 ? <div className="px-3 text-sm text-gray2">{t("chat.noOthers")}</div> :
          sortedPeople.map(p => {
            const hasUnread = (unread[threadKey("dm", p.id)] ?? 0) > 0;
            return (
            <button
              key={p.id}
              onClick={() => pickConversation({ kind: "dm", id: p.id, name: `${p.first_name ?? p.email} ${p.last_name ?? ""}`.trim() })}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm transition ${
                active?.kind === "dm" && active.id === p.id ? "bg-teal-mint text-teal-dark font-semibold" : `hover:bg-offwhite ${hasUnread ? "font-semibold text-charcoal" : "text-charcoal"}`
              }`}
            >
              <EmployeeAvatar path={p.profile_picture_path} firstName={p.first_name} lastName={p.last_name} email={p.email} size={28} />
              <span className="truncate">{p.first_name || p.email}</span>
              {hasUnread && <span aria-label="unread" className="ml-auto h-2 w-2 shrink-0 rounded-full bg-teal-dark" />}
            </button>
            );
          })
        }
      </aside>

      {groupModal && (
        <GroupChatModal
          people={people}
          onClose={() => setGroupModal(false)}
          onCreated={async (channelId, name) => {
            setGroupModal(false);
            await loadSidebar();
            pickConversation({ kind: "channel", id: channelId, name: name || "Group chat" });
          }}
        />
      )}

      {manageGroup && (
        <ManageGroupModal
          channel={manageGroup}
          people={people}
          meId={employee?.id ?? null}
          myRole={employee?.role ?? null}
          onClose={() => setManageGroup(null)}
          onSaved={async (name) => {
            setManageGroup(null);
            await loadSidebar();
            setActive(a => (a && a.kind === "channel" && a.id === manageGroup.id ? { ...a, name: name || "Group chat" } : a));
          }}
          onDeleted={async () => {
            setManageGroup(null);
            if (active?.kind === "channel" && active.id === manageGroup.id) { setActive(null); setMessages([]); }
            await loadSidebar();
          }}
        />
      )}

      <section className="flex min-w-0 flex-1 flex-col bg-offwhite">
        <header className="flex items-center justify-between border-b border-line bg-white px-3 py-3 md:px-5">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setSidebarOpen(s => !s)}
              className="rounded-lg p-1.5 text-charcoal hover:bg-offwhite md:hidden"
              aria-label="Toggle conversations"
            >
              <Menu size={20} />
            </button>
            <h2 className="truncate text-base font-semibold">{active?.name ?? t("chat.selectConversation")}</h2>
          </div>
          <div className="flex items-center gap-2">
            {activeGroup && canManageGroup && (
              <button onClick={() => setManageGroup(activeGroup)} className="btn-secondary flex items-center gap-1 text-xs" title="Edit group members">
                <Users size={14} /> <span className="hidden sm:inline">Members</span>
              </button>
            )}
            <button onClick={() => void startHuddle()} disabled={!active} className="btn-secondary flex items-center gap-1 text-xs">
              <Video size={14} /> <span className="hidden sm:inline">{t("chat.startHuddle")}</span><span className="sm:hidden">{t("chat.startHuddle")}</span>
            </button>
          </div>
        </header>
        {huddleInfo && (
          <div className="border-b border-line bg-teal-mint/30 px-5 py-2 text-xs text-teal-dark flex items-center gap-2">
            <Video size={12} /> {t("chat.huddleRunning")} <code className="rounded bg-white px-1">{huddleInfo.url}</code>
            <button onClick={() => { void navigator.clipboard.writeText(huddleInfo.url); }} className="ml-1 hover:underline"><CopyIcon size={12} className="inline" /> {t("chat.copy")}</button>
            <button onClick={() => setHuddleInfo(null)} className="ml-auto text-gray2 hover:text-charcoal">×</button>
          </div>
        )}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-5">
          {hasOlder && (
            <div className="flex justify-center pb-1">
              <button
                onClick={() => void loadOlder()}
                disabled={loadingOlder}
                className="btn-secondary text-xs"
              >
                {loadingOlder ? "Loading…" : "Load older messages"}
              </button>
            </div>
          )}
          {messages.length === 0 && active && (
            <div className="py-12 text-center text-sm text-gray2">{t("chat.noMessages")}</div>
          )}
          {messages.map((m, i) => {
            const isMe = m.sender_id === employee?.id;
            const sender = isMe ? employee : peopleMap[m.sender_id];
            const replyToId = (m as { reply_to_id?: string | null }).reply_to_id ?? null;
            const parent = replyToId ? messagesById.get(replyToId) : undefined;
            const divider = needsDivider(messages[i - 1]?.created_at, m.created_at);
            return (
              <div key={m.id}>
              {divider && (
                <div className="my-4 flex items-center gap-3">
                  <div className="h-px flex-1 bg-line" />
                  <span className="rounded-full bg-offwhite px-3 py-0.5 text-[11px] font-medium text-gray2">{dayDividerLabel(m.created_at)}</span>
                  <div className="h-px flex-1 bg-line" />
                </div>
              )}
              <div className={`group flex gap-3 ${isMe ? "flex-row-reverse" : ""}`}>
                {sender ? (
                  <EmployeeAvatar path={sender.profile_picture_path} firstName={sender.first_name} lastName={sender.last_name} email={sender.email} size={36} />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-offwhite text-xs font-semibold text-charcoal">?</span>
                )}
                <div className={`max-w-[70%] ${isMe ? "items-end" : ""} flex flex-col`}>
                  {replyToId && (
                    parent ? (
                      <div className={`mb-1 max-w-full rounded-lg border-l-2 border-line bg-black/5 px-2 py-1 text-[11px] text-gray2 ${isMe ? "self-end" : ""}`}>
                        <span className="font-semibold text-charcoal">{senderName(parent)}</span>
                        <span className="ml-1 break-words">{snippet(parent) || "…"}</span>
                      </div>
                    ) : (
                      <div className={`mb-1 rounded-lg border-l-2 border-line bg-black/5 px-2 py-1 text-[11px] italic text-gray2 ${isMe ? "self-end" : ""}`}>
                        Replied to a message
                      </div>
                    )
                  )}
                  <div className={`flex items-center gap-1.5 ${isMe ? "flex-row-reverse" : ""}`}>
                    <div className={`rounded-2xl px-4 py-2 text-sm shadow-sm ${isMe ? "bg-teal-dark text-white" : "bg-white"} whitespace-pre-wrap break-words`}>
                      {m.content && renderMessageContent(m.content)}
                      {m.attachment_path && <ChatAttachment msg={m} isMe={isMe} />}
                    </div>
                    <button
                      onClick={() => setReplyingTo(m)}
                      className="shrink-0 rounded-full p-1 text-gray2 opacity-0 transition hover:bg-offwhite hover:text-charcoal focus:opacity-100 group-hover:opacity-100"
                      aria-label="Reply"
                      title="Reply"
                    >
                      <Reply size={14} />
                    </button>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-gray2">
                    <span>{sender?.first_name || sender?.email}</span>
                    <span>·</span>
                    <span>{fmtTime(m.created_at)}</span>
                    {isMe && <button onClick={() => deleteMessage(m.id)} className="hover:text-bad">{t("chat.delete")}</button>}
                  </div>
                </div>
              </div>
              </div>
            );
          })}
        </div>
        <footer className="border-t border-line bg-white p-3 relative">
          {mentionState.open && mentionMatches.length > 0 && (
            <div className="absolute bottom-full left-3 mb-1 w-64 rounded-lg border border-line bg-white shadow-soft overflow-hidden">
              {mentionMatches.map((p, i) => (
                <button
                  key={p.id}
                  onMouseDown={e => { e.preventDefault(); pickMention(p); }}
                  onMouseEnter={() => setMentionIdx(i)}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${i === mentionIdx ? "bg-teal-mint text-teal-dark font-semibold" : "text-charcoal hover:bg-offwhite"}`}
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-semibold text-charcoal ring-1 ring-line">{initials(p.first_name, p.last_name, p.email)}</span>
                  <span className="truncate">@{p.username || p.first_name || p.email.split("@")[0]}</span>
                  <span className={`ml-auto truncate text-xs ${i === mentionIdx ? "text-teal-dark/80" : "text-gray2"}`}>{[p.first_name, p.last_name].filter(Boolean).join(" ")}</span>
                </button>
              ))}
            </div>
          )}
          {sendError && (
            <div className="mb-2 rounded bg-bad-bg px-3 py-1.5 text-xs text-bad flex items-center justify-between">
              <span>⚠ {sendError}</span>
              <button onClick={() => setSendError(null)} className="text-bad hover:underline">dismiss</button>
            </div>
          )}
          {replyingTo && (
            <div className="mb-2 flex items-center gap-2 rounded-lg border-l-2 border-teal-dark bg-offwhite px-3 py-1.5 text-xs text-charcoal">
              <Reply size={14} className="shrink-0 text-teal-dark" />
              <span className="min-w-0 flex-1 truncate">
                <span className="font-semibold">Replying to {senderName(replyingTo)}:</span>{" "}
                <span className="text-gray2">{snippet(replyingTo) || "…"}</span>
              </span>
              <button
                type="button"
                onClick={() => setReplyingTo(null)}
                className="shrink-0 text-gray2 hover:text-bad"
                aria-label="Cancel reply"
              >
                <X size={14} />
              </button>
            </div>
          )}
          {pendingFile && (
            <div className="mb-2 flex items-center gap-2 rounded-lg bg-offwhite px-3 py-1.5 text-xs text-charcoal">
              <Paperclip size={14} className="shrink-0 text-gray2" />
              <span className="min-w-0 flex-1 truncate">{pendingFile.name}</span>
              <span className="shrink-0 text-gray2">{formatBytes(pendingFile.size)}</span>
              <button
                type="button"
                onClick={() => { setPendingFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                className="shrink-0 text-gray2 hover:text-bad"
                aria-label="Remove attachment"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={e => pickFile(e.target.files?.[0] ?? null)}
          />
          <form onSubmit={e => { e.preventDefault(); void send(); }} className="flex items-end gap-2 pr-14 md:pr-0">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={!active || sending}
              className="btn-secondary shrink-0 px-3"
              aria-label="Attach a file"
              title="Attach a file"
            >
              <Paperclip size={18} />
            </button>
            <div className="relative shrink-0">
              <button
                type="button"
                onMouseDown={e => e.preventDefault()}
                onClick={() => setEmojiPickerOpen(o => !o)}
                disabled={!active || sending}
                className="btn-secondary px-3"
                aria-label="Insert emoji"
                title="Insert emoji"
              >
                <Smile size={18} />
              </button>
              {emojiPickerOpen && (
                <>
                  {/* Same click-outside-to-close idiom as the huddle picker and group
                      modal below — a full-screen transparent layer under the popover. */}
                  <div className="fixed inset-0 z-10" onClick={() => setEmojiPickerOpen(false)} />
                  <div className="absolute bottom-full left-0 z-20 mb-1 grid w-64 grid-cols-8 gap-1 rounded-lg border border-line bg-white p-2 shadow-soft">
                    {EMOJI_LIST.map(e => (
                      <button
                        key={e}
                        type="button"
                        onMouseDown={ev => { ev.preventDefault(); insertEmoji(e); }}
                        className="rounded p-1 text-lg leading-none hover:bg-offwhite"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <textarea
              ref={inputRef}
              rows={1}
              // The height is set by the effect above. `resize-none` removes the
              // drag handle, which would otherwise fight that effect.
              className="input resize-none leading-6"
              placeholder={composerLabel}
              aria-label={composerLabel}
              value={draft}
              onChange={e => onDraftChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
              onPaste={e => {
                // A pasted screenshot becomes the pending attachment — same
                // path as the paperclip. Text pastes stay with the browser.
                const f = pastedImage(e);
                if (!f) return;
                e.preventDefault();
                pickFile(f);
              }}
              onKeyDown={e => {
                // Mention dropdown navigation
                if (mentionState.open && mentionMatches.length > 0) {
                  if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => Math.min(mentionMatches.length - 1, i + 1)); return; }
                  if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx(i => Math.max(0, i - 1)); return; }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    pickMention(mentionMatches[mentionIdx]);
                    return;
                  }
                  if (e.key === "Escape") {
                    setMentionState({ open: false, query: "", start: 0 });
                    return;
                  }
                }
                // Plain Enter (no Shift) submits the message
                if (e.key === "Enter" && !e.shiftKey && !(e.nativeEvent as KeyboardEvent).isComposing) {
                  e.preventDefault();
                  void send();
                }
              }}
              disabled={!active}
            />
            <button type="submit" className="btn shrink-0" disabled={!active || sending || (!draft.trim() && !pendingFile)}>
              {sending ? "…" : t("chat.send")}
            </button>
          </form>
        </footer>
      </section>

      {huddlePicker && active && (
        <ChatHuddlePicker
          people={people}
          selfId={employee?.id ?? ""}
          activeName={active.name}
          onCancel={() => setHuddlePicker(false)}
          onLaunch={launchHuddle}
        />
      )}
    </div>
  );
}

function ChatHuddlePicker({ people, selfId, activeName, onCancel, onLaunch }: {
  people: Employee[]; selfId: string; activeName: string;
  onCancel: () => void; onLaunch: (ids: string[]) => Promise<void>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = people.filter(p => p.id !== selfId).filter(p => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (p.first_name || "").toLowerCase().includes(s)
      || (p.last_name || "").toLowerCase().includes(s)
      || p.email.toLowerCase().includes(s)
    );
  });
  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function start() {
    setBusy(true);
    await onLaunch([...selected]);
    setBusy(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onCancel}>
      <div className="card w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="mb-1 text-lg font-semibold">Start huddle</h2>
        <p className="mb-3 text-xs text-gray2">
          A Jitsi room opens in a new tab and the link drops into <strong>{activeName}</strong>. Pick anyone to ping so they get a notification with the same link — they'll land in your room.
        </p>
        <input className="input mb-3" placeholder="Search teammates to ping…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
        <div className="max-h-[40vh] overflow-y-auto space-y-1">
          {filtered.map(p => (
            <label key={p.id} className={`flex items-center gap-2 rounded-lg px-3 py-2 cursor-pointer text-sm ${selected.has(p.id) ? "bg-teal-mint text-teal-dark" : "text-charcoal hover:bg-offwhite"}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
              <span className="flex-1">{[p.first_name, p.last_name].filter(Boolean).join(" ") || p.email}</span>
              <span className="text-xs text-gray2">{p.email}</span>
            </label>
          ))}
          {filtered.length === 0 && <div className="px-3 py-4 text-center text-sm text-gray2">No matches.</div>}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="btn-secondary">Cancel</button>
          <button onClick={() => void start()} disabled={busy} className="btn">
            {busy ? "Starting…" : selected.size > 0 ? `Start & ping ${selected.size}` : "Start huddle"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pick multiple people and spin up a private group chat in one step. The group
// is a private channel (hq_create_group_chat RPC); only its members can see it.
function GroupChatModal({ people, onClose, onCreated }: {
  people: Employee[]; onClose: () => void; onCreated: (channelId: string, name: string) => void | Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const filtered = people.filter(p => `${p.first_name ?? ""} ${p.last_name ?? ""} ${p.email ?? ""}`.toLowerCase().includes(q.toLowerCase()));

  function toggle(id: string) {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
    if (!nameTouched) setName(people.filter(p => n.has(p.id)).map(p => p.first_name || p.email).filter(Boolean).join(", "));
  }

  async function create() {
    if (selected.size < 1) { setErr("Pick at least one person."); return; }
    setBusy(true); setErr(null);
    const { data, error } = await supabase.rpc("hq_create_group_chat", { p_name: name.trim() || null, p_member_ids: Array.from(selected) });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    await onCreated(data as string, name.trim());
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="card max-h-[85vh] w-full max-w-md overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">New group chat</h2><CloseButton onClick={onClose} /></div>
        {err && <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{err}</div>}
        <div className="space-y-3">
          <div>
            <label className="label">Group name <span className="font-normal normal-case text-gray2">(optional)</span></label>
            <input className="input" value={name} onChange={e => { setName(e.target.value); setNameTouched(true); }} placeholder="e.g. Sales leads" />
          </div>
          <div>
            <label className="label">Add people {selected.size > 0 ? `(${selected.size})` : ""}</label>
            <input className="input mb-2" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-line p-1">
              {filtered.length === 0 ? <div className="px-2 py-4 text-center text-sm text-gray2">No one found.</div> :
                filtered.map(p => (
                  <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-offwhite">
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                    <EmployeeAvatar path={p.profile_picture_path} firstName={p.first_name} lastName={p.last_name} email={p.email} size={26} />
                    <span className="truncate">{`${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email}</span>
                  </label>
                ))}
            </div>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={() => void create()} disabled={busy || selected.size < 1} className="btn">{busy ? "Creating…" : "Create group"}</button>
        </div>
      </div>
    </div>
  );
}

// Edit an existing group chat: rename it, add/remove people, or (owner only)
// delete it outright. Every write goes through a SECURITY DEFINER RPC that
// re-checks permission server-side — the UI gating here is convenience only.
function ManageGroupModal({ channel, people, meId, myRole, onClose, onSaved, onDeleted }: {
  channel: ChatChannel;
  people: Employee[];
  meId: string | null;
  myRole: string | null;
  onClose: () => void;
  onSaved: (name: string) => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [name, setName] = useState(channel.name ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isOwner = !!meId && channel.created_by === meId;
  const canDelete = isOwner || myRole === "super_admin";

  useEffect(() => {
    let cancelled = false;
    void supabase
      .from("hq_chat_channel_members")
      .select("employee_id")
      .eq("channel_id", channel.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) setErr(error.message);
        setSelected(new Set(((data as { employee_id: string }[]) ?? []).map(r => r.employee_id)));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [channel.id]);

  // `people` excludes me, so add myself back — I may need to leave the group,
  // and an admin editing someone else's group needs to see the full roster.
  const roster = useMemo(() => {
    const list = [...people];
    const me = meId && !list.some(p => p.id === meId) ? { id: meId } as Employee : null;
    return me ? [me, ...list] : list;
  }, [people, meId]);

  const filtered = roster.filter(p =>
    `${p.first_name ?? ""} ${p.last_name ?? ""} ${p.email ?? ""}`.toLowerCase().includes(q.toLowerCase()));

  function toggle(id: string) {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  }

  async function save() {
    setBusy(true); setErr(null);
    const members = Array.from(selected);
    const { error: mErr } = await supabase.rpc("hq_set_group_members", { p_channel: channel.id, p_member_ids: members });
    if (mErr) { setBusy(false); setErr(mErr.message); return; }
    if ((name.trim() || null) !== (channel.name || null)) {
      const { error: nErr } = await supabase.rpc("hq_rename_group_chat", { p_channel: channel.id, p_name: name.trim() || null });
      if (nErr) { setBusy(false); setErr(nErr.message); return; }
    }
    setBusy(false);
    await onSaved(name.trim());
  }

  async function remove() {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc("hq_delete_group_chat", { p_channel: channel.id });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    await onDeleted();
  }

  const ownerName = (() => {
    const o = roster.find(p => p.id === channel.created_by);
    return o ? (`${o.first_name ?? ""} ${o.last_name ?? ""}`.trim() || o.email) : null;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4" onClick={onClose}>
      <div className="card max-h-[85vh] w-full max-w-md overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Group settings</h2>
          <CloseButton onClick={onClose} />
        </div>
        {err && <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-sm text-bad">{err}</div>}
        <div className="space-y-3">
          <div>
            <label className="label">Group name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Group chat" />
            {ownerName && <p className="mt-1 text-xs text-gray2">Owner: {ownerName}{isOwner ? " (you)" : ""}</p>}
          </div>
          <div>
            <label className="label">Members {selected.size > 0 ? `(${selected.size})` : ""}</label>
            <input className="input mb-2" placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} />
            <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-line p-1">
              {loading ? <div className="px-2 py-4 text-center text-sm text-gray2">Loading…</div> :
                filtered.length === 0 ? <div className="px-2 py-4 text-center text-sm text-gray2">No one found.</div> :
                filtered.map(p => {
                  const isChannelOwner = p.id === channel.created_by;
                  return (
                    <label key={p.id} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-offwhite ${isChannelOwner ? "opacity-70" : "cursor-pointer"}`}>
                      <input
                        type="checkbox"
                        checked={isChannelOwner || selected.has(p.id)}
                        disabled={isChannelOwner}
                        title={isChannelOwner ? "The group owner can't be removed" : undefined}
                        onChange={() => toggle(p.id)}
                      />
                      <EmployeeAvatar path={p.profile_picture_path} firstName={p.first_name} lastName={p.last_name} email={p.email} size={26} />
                      <span className="truncate">{`${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || p.email}{p.id === meId ? " (you)" : ""}</span>
                      {isChannelOwner && <span className="ml-auto shrink-0 text-[11px] uppercase tracking-wide text-gray2">Owner</span>}
                    </label>
                  );
                })}
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2">
          <div>
            {canDelete && (
              confirmDelete ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-bad">Delete for everyone?</span>
                  <button onClick={() => void remove()} disabled={busy} className="rounded-lg bg-bad px-2 py-1 font-medium text-white">{busy ? "Deleting…" : "Yes, delete"}</button>
                  <button onClick={() => setConfirmDelete(false)} className="text-gray2 hover:text-charcoal">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="text-sm font-medium text-bad hover:underline">Delete group</button>
              )
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button onClick={() => void save()} disabled={busy || loading} className="btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
